// Globals
import { useTranslation } from 'react-i18next';

// Components
import { FormItem, IFormInstance, useWatchForm } from '@/components';
import { Input, InputNumber, Radio } from '@/ui';
import {
  StyledForm,
  StyledFormItemsBlock,
  StyledDivider,
  StyledRadioGroup,
  StyledSelect,
  StyledUnitsSelect,
} from './CreateProductFormStyles';
import ProductPhotosField from '../ProductPhotosField/ProductPhotosField';

// Modules
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';

// Models
import { SupportedLocales } from '@/models/localization.model';

// Helpers
import {
  CreateProductFormFieldsNames,
  getUnitsOptions,
  getPackageQuantityLabel,
  isServiceUnit,
  isIntegerUnit,
  MeasureUnitCode,
} from '../../helpers';
import { useCreateProductValidationRules } from '../../hooks';

const initialLocale = {
  [SupportedLocales.UK]: { name: '', description: '' },
  [SupportedLocales.RU]: { name: '', description: '' },
};

interface ICreateProductForm {
  handleSubmit: () => void;
  formInstance: IFormInstance;
  storesOptions: { value: string; label: string }[];
  onStoresSearch: (name: string) => void;
}

const CreateProductForm = ({ handleSubmit, formInstance, storesOptions, onStoresSearch }: ICreateProductForm) => {
  const { t } = useTranslation(['products', 'common']);

  const rules = useCreateProductValidationRules();
  const placeholder = t('common_placeholder_input', { ns: 'common' });

  const unitCode = useWatchForm<MeasureUnitCode | undefined>(CreateProductFormFieldsNames.UNIT_CODE, formInstance);
  const showPackageFields = !isServiceUnit(unitCode);
  const packageQuantityLabel = getPackageQuantityLabel(unitCode, t);
  const isIntegerQuantity = isIntegerUnit(unitCode);

  return (
    <StyledForm initialValues={{ locale: initialLocale }} onFinish={handleSubmit} form={formInstance}>
      <FormItem
        name={CreateProductFormFieldsNames.STORE_ID}
        label={t('create_product_store_select_label')}
        rules={rules[CreateProductFormFieldsNames.STORE_ID]}
      >
        <StyledSelect allowClear showSearch options={storesOptions} onSearch={onStoresSearch} />
      </FormItem>
      <FormItem style={{ width: '100%' }} name={LOCALES_FIELD_NAME}>
        <LocalesFormFields
          labelPrefix={'main_info_product_'}
          ns={'products'}
          textAreaFields={['description']}
          rules={rules[CreateProductFormFieldsNames.LOCALE]}
        />
      </FormItem>
      <FormItem
        name={CreateProductFormFieldsNames.SLUG}
        label={t('main_info_product_slug_label')}
        rules={rules[CreateProductFormFieldsNames.SLUG]}
      >
        <Input placeholder={placeholder} />
      </FormItem>
      <StyledFormItemsBlock>
        <FormItem
          name={CreateProductFormFieldsNames.EXTERNAL_ID}
          label={t('products_list_partner_sku')}
          rules={rules[CreateProductFormFieldsNames.EXTERNAL_ID]}
        >
          <Input placeholder={placeholder} />
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem
          name={CreateProductFormFieldsNames.UKTZED}
          label={t('main_info_product_uksd_label')}
          rules={rules[CreateProductFormFieldsNames.UKTZED]}
        >
          <Input />
        </FormItem>

        <FormItem
          name={CreateProductFormFieldsNames.IMPORTED}
          label={t('main_info_product_imported_label')}
          rules={rules[CreateProductFormFieldsNames.IMPORTED]}
        >
          <StyledRadioGroup>
            <Radio value={true}>{t('regions_and_prices_yes_title')}</Radio>
            <Radio value={false}>{t('regions_and_prices_non_title')}</Radio>
          </StyledRadioGroup>
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem
          name={CreateProductFormFieldsNames.WEIGHT}
          label={t('main_info_product_weight')}
          rules={rules[CreateProductFormFieldsNames.WEIGHT]}
        >
          <InputNumber />
        </FormItem>

        <FormItem
          name={CreateProductFormFieldsNames.LENGTH}
          label={t('main_info_product_length')}
          rules={rules[CreateProductFormFieldsNames.LENGTH]}
        >
          <InputNumber />
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem
          name={CreateProductFormFieldsNames.HEIGHT}
          label={t('main_info_product_height')}
          rules={rules[CreateProductFormFieldsNames.HEIGHT]}
        >
          <InputNumber />
        </FormItem>

        <FormItem
          name={CreateProductFormFieldsNames.WIDTH}
          label={t('main_info_product_width')}
          rules={rules[CreateProductFormFieldsNames.WIDTH]}
        >
          <InputNumber />
        </FormItem>
      </StyledFormItemsBlock>

      <FormItem
        label={t('product_unit_name_label')}
        name={CreateProductFormFieldsNames.UNIT_CODE}
        rules={rules[CreateProductFormFieldsNames.UNIT_CODE]}
      >
        <StyledUnitsSelect options={getUnitsOptions(t)} />
      </FormItem>

      {showPackageFields && (
        <StyledFormItemsBlock>
          <FormItem name={CreateProductFormFieldsNames.PACKAGE_QUANTITY} label={packageQuantityLabel} initialValue={1}>
            <InputNumber
              min={isIntegerQuantity ? 1 : 0.0001}
              step={isIntegerQuantity ? 1 : 0.01}
              precision={isIntegerQuantity ? 0 : 2}
              style={{ width: '100%' }}
            />
          </FormItem>

          <FormItem
            name={CreateProductFormFieldsNames.MIN_ORDER_PACKAGES}
            label={t('min_order_packages_label')}
            initialValue={1}
          >
            <InputNumber min={1} step={1} precision={0} style={{ width: '100%' }} />
          </FormItem>
        </StyledFormItemsBlock>
      )}

      <StyledFormItemsBlock>
        <FormItem
          name={CreateProductFormFieldsNames.IS_CUSTOM_ORDER}
          label={t('main_info_product_is_custom_order_label')}
          rules={rules[CreateProductFormFieldsNames.IS_CUSTOM_ORDER]}
        >
          <StyledRadioGroup>
            <Radio value={true}>{t('regions_and_prices_yes_title')}</Radio>
            <Radio value={false}>{t('regions_and_prices_non_title')}</Radio>
          </StyledRadioGroup>
        </FormItem>
      </StyledFormItemsBlock>
      <StyledDivider />
      <ProductPhotosField formInstance={formInstance} />
    </StyledForm>
  );
};

export default CreateProductForm;
